import api from '@/lib/api/api';
import type { FetchResponse } from '@/lib/api/api';
import type { Activity } from '@/lib/models/activity';
import type { ODataResponse } from '@/lib/models/common';

/**
 * Interfaccia per l'endpoint delle Activities
 */
export interface ActivityEndpoints {
	/**
	 * Recupera la lista delle attività con query OData
	 * @param query - Query string OData (es: "?$top=25&$count=true&$orderby=description asc")
	 * @returns Promise con response OData contenente array di Activity
	 */
	getActivities(query: string): Promise<FetchResponse<ODataResponse<Activity>>>;

	/**
	 * Recupera una singola attività per ID
	 * @param id - ID dell'attività
	 * @returns Promise con singola Activity
	 */
	getActivity(id: number): Promise<FetchResponse<Activity>>;

	/**
	 * Aggiorna un'attività esistente
	 * @param id - ID dell'attività da aggiornare
	 * @param payload - Dati da aggiornare (il backend ignora id e metadata)
	 * @returns Promise con Activity aggiornata
	 */
	updateActivity(id: number, payload: Activity): Promise<FetchResponse<Activity>>;

	/**
	 * Crea una nuova attività
	 * @param payload - Dati della nuova attività (il backend ignora id e metadata)
	 * @returns Promise con Activity creata
	 */
	postActivity(payload: Activity): Promise<FetchResponse<Activity>>;

	/**
	 * Elimina un'attività
	 * @param id - ID dell'attività da eliminare
	 * @returns Promise con response di conferma
	 */
	deleteActivity(id: number): Promise<FetchResponse<void>>;
}

/**
 * Implementazione tipizzata degli endpoint per le Activities
 */
const activityEndpoints: ActivityEndpoints = {
	getActivities(query: string): Promise<FetchResponse<ODataResponse<Activity>>> {
		return api.get(`/odata/v1/JobActivities${query}`);
	},

	getActivity(id: number): Promise<FetchResponse<Activity>> {
		return api.get(`/odata/v1/JobActivities/${id}`);
	},

	updateActivity(id: number, payload: Activity): Promise<FetchResponse<Activity>> {
		return api.put(`/api/v1/JobActivities/${id}`, payload);
	},

	postActivity(payload: Activity): Promise<FetchResponse<Activity>> {
		return api.post('/api/v1/JobActivities', payload);
	},

	deleteActivity(id: number): Promise<FetchResponse<void>> {
		return api.delete(`/api/v1/JobActivities/${id}`);
	},
};

export default activityEndpoints;
